import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import './MyReports.css';

function MyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch('/api/reports/my-reports', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports);
      } else {
        setMessage('Failed to load reports');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setMessage('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', text: 'Pending Review' },
      reviewed: { class: 'status-reviewed', text: 'Reviewed' },
      resolved: { class: 'status-resolved', text: 'Resolved' },
      dismissed: { class: 'status-dismissed', text: 'Dismissed' }
    };
    return badges[status] || badges.pending;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      critical: { class: 'priority-critical', text: 'Critical' },
      high: { class: 'priority-high', text: 'High' },
      normal: { class: 'priority-normal', text: 'Normal' },
      low: { class: 'priority-low', text: 'Low' }
    };
    return badges[priority] || badges.normal;
  };

  const formatCategory = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="my-reports-container">
        <div className="loading">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="my-reports-container">
      <div className="reports-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <h1>📋 My Reports</h1>
        <p>Track the status of reports you've submitted.</p>
      </div>

      {message && (
        <div className="message error">{message}</div>
      )}

      {reports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          <h2>No Reports Submitted</h2>
          <p>You haven't reported any users yet.</p>
          <p className="empty-hint">
            If you encounter inappropriate behavior, you can report users from their profile page.
          </p>
        </div>
      ) : (
        <div className="reports-list">
          {reports.map((report) => {
            const statusBadge = getStatusBadge(report.status);
            const priorityBadge = getPriorityBadge(report.priority);
            
            return (
              <div key={report.id} className="report-card">
                <div className="report-header-row">
                  <div className="report-user">
                    <div className="user-avatar">
                      {report.reportedUserPhoto ? (
                        <img src={report.reportedUserPhoto} alt={report.reportedUserName} />
                      ) : (
                        <div className="avatar-placeholder">
                          {report.reportedUserName?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3>{report.reportedUserName || 'Unknown User'}</h3>
                      <p className="report-category">{formatCategory(report.category)}</p>
                    </div>
                  </div>
                  <div className="report-badges">
                    <span className={`badge ${statusBadge.class}`}>
                      {statusBadge.text}
                    </span>
                    <span className={`badge ${priorityBadge.class}`}>
                      {priorityBadge.text}
                    </span>
                  </div>
                </div>

                <div className="report-details">
                  <p><strong>Details:</strong> {report.details}</p>
                  <p className="report-date">
                    Submitted {new Date(report.createdAt).toLocaleDateString()}
                  </p>
                  {report.reviewedAt && (
                    <p className="report-date">
                      Reviewed {new Date(report.reviewedAt).toLocaleDateString()}
                    </p>
                  )}
                  {report.actionTaken && (
                    <div className="action-taken">
                      <strong>Action Taken:</strong> {report.actionTaken}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="info-section">
        <h3>About Reports</h3>
        <ul>
          <li>Reports are reviewed by our moderation team within 24-48 hours</li>
          <li>Critical reports (fraud, violence) are prioritized</li>
          <li>Your identity is kept confidential</li>
          <li>You'll be notified when action is taken</li>
          <li>False reports may result in account suspension</li>
        </ul>
      </div>
    </div>
  );
}

export default MyReports;
